import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FormField, Input } from '../common/FormField';
import Button from '../common/Button';

const initialState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(initialState);
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const redirectTimeoutRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFormValues(initialState);
      setFieldErrors({});
      setServerError('');
      setSuccessMessage('');
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const validateFields = () => {
    const errors = {};
    if (!formValues.currentPassword.trim()) {
      errors.currentPassword = 'Current password is required';
    }
    if (!formValues.newPassword.trim()) {
      errors.newPassword = 'New password is required';
    } else if (formValues.newPassword.trim().length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    if (!formValues.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm the new password';
    } else if (formValues.confirmPassword.trim() !== formValues.newPassword.trim()) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
    setFieldErrors((prev) => ({
      ...prev,
      [name]: ''
    }));
    setServerError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateFields()) {
      return;
    }
    setIsSubmitting(true);
    setServerError('');

    try {
      await api.post('/auth/change-password', {
        currentPassword: formValues.currentPassword.trim(),
        newPassword: formValues.newPassword.trim(),
        confirmPassword: formValues.confirmPassword.trim()
      });

      setSuccessMessage('Password updated successfully. Redirecting to login...');
      redirectTimeoutRef.current = setTimeout(async () => {
        await logout();
        navigate('/login', { replace: true });
      }, 1500);
    } catch (error) {
      const apiError =
        error.response?.data?.error ||
        error.response?.data?.errors?.[0]?.msg ||
        'Unable to change password. Please try again.';
      setServerError(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleLabel = user?.currentRole ? `${user.currentRole} role password` : 'selected role password';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-password-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-800 shadow-2xl max-h-dvh-90 flex flex-col border border-transparent dark:border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 pb-0 mb-6 flex items-center justify-between shrink-0">
          <div>
            <h2 id="change-password-title" className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              Change Password
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Update your {roleLabel}. You will be logged out after saving.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 shrink-0 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center"
            aria-label="Close change password modal"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <form className="space-y-4 px-6 pb-6 overflow-y-auto" onSubmit={handleSubmit}>
          <FormField label="Current Password" htmlFor="currentPassword" error={fieldErrors.currentPassword}>
            <Input
              id="currentPassword"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              placeholder="Enter current password"
              value={formValues.currentPassword}
              onChange={handleInputChange}
              disabled={isSubmitting}
              error={fieldErrors.currentPassword}
            />
          </FormField>

          <FormField label="New Password" htmlFor="newPassword" error={fieldErrors.newPassword}>
            <Input
              id="newPassword"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              placeholder="Enter new password"
              value={formValues.newPassword}
              onChange={handleInputChange}
              disabled={isSubmitting}
              error={fieldErrors.newPassword}
            />
          </FormField>

          <FormField label="Confirm Password" htmlFor="confirmPassword" error={fieldErrors.confirmPassword}>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              placeholder="Confirm new password"
              value={formValues.confirmPassword}
              onChange={handleInputChange}
              disabled={isSubmitting}
              error={fieldErrors.confirmPassword}
            />
          </FormField>

          {serverError && (
            <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-400" role="alert">
              {serverError}
            </div>
          )}

          {successMessage && (
            <div className="rounded-xl border border-green-200 dark:border-emerald-500/30 bg-green-50 dark:bg-emerald-500/10 px-4 py-3 text-sm text-green-600 dark:text-emerald-400" role="status">
              {successMessage}
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;


